import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, Lock, Eye, TrendingUp, TrendingDown, Minus,
  Sparkles, Target, Users, ExternalLink
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadialBarChart, RadialBar
} from 'recharts';

const PILLAR_COLORS = {
  Authenticity: '#a855f7',
  Accountability: '#10b981',
  Community: '#3b82f6'
};

function SharedDashboardPage() {
  const { shareToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    loadSharedDashboard();
  }, [shareToken]);

  const loadSharedDashboard = async (pwd = null) => {
    try {
      setLoading(true);
      setError('');
      
      const url = pwd 
        ? `/shared/${shareToken}?password=${encodeURIComponent(pwd)}`
        : `/shared/${shareToken}`;
      
      // Use direct API call (no auth header needed)
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api${url}`);
      const data = await response.json();
      
      if (!data.success) {
        if (data.needs_password) {
          setNeedsPassword(true);
          setError('');
        } else {
          setError(data.error || 'Failed to load shared dashboard');
        }
        return;
      }
      
      setDashboardData(data.data);
      setNeedsPassword(false);
    } catch (err) {
      console.error('Failed to load shared dashboard:', err);
      setError('Failed to load shared dashboard. The link may be invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    loadSharedDashboard(password);
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 70) return 'from-green-500 to-emerald-600';
    if (score >= 50) return 'from-yellow-500 to-amber-600';
    return 'from-red-500 to-rose-600';
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto"></div>
          <p className="mt-4 text-purple-200">Loading shared dashboard...</p>
        </div>
      </div>
    );
  }

  // Password required state
  if (needsPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 w-full max-w-md border border-white/20">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-purple-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Password Protected</h1>
            <p className="text-purple-200 mt-2">Enter the password to view this dashboard</p>
          </div>
          
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}
          
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4"
              autoFocus
              data-testid="share-password-input"
            />
            <button
              type="submit"
              className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition"
              data-testid="share-password-submit"
            >
              View Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 w-full max-w-md border border-white/20 text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Eye className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Unable to Load Dashboard</h1>
          <p className="text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  // No data state
  if (!dashboardData || !dashboardData.has_data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 w-full max-w-md border border-white/20 text-center">
          <Shield className="w-16 h-16 text-purple-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">{dashboardData?.organization_name}</h1>
          <p className="text-gray-300">No trust score data available yet.</p>
        </div>
      </div>
    );
  }

  // Main dashboard view
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <header className="bg-black/30 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">{dashboardData.organization_name}</h1>
                <p className="text-sm text-purple-300">Trust Index Dashboard</p>
              </div>
            </div>
            <div className="text-right text-sm text-gray-400">
              <p>Read-only view</p>
              {dashboardData.last_updated && (
                <p>Updated: {new Date(dashboardData.last_updated).toLocaleDateString()}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Trust Score Hero */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-white/20" data-testid="trust-score-card">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <p className="text-purple-300 uppercase tracking-wider text-sm mb-2">Overall Trust Score</p>
              <p className={`text-7xl font-bold bg-gradient-to-r ${getScoreBg(dashboardData.trust_score)} bg-clip-text text-transparent`}>
                {dashboardData.trust_score?.toFixed(1)}
              </p>
              <p className="text-gray-400 mt-2">out of 100</p>
            </div>
            
            <div className="w-48 h-48">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart 
                  innerRadius="70%" 
                  outerRadius="100%" 
                  data={[{ value: dashboardData.trust_score, fill: '#8b5cf6' }]}
                  startAngle={180}
                  endAngle={0}
                >
                  <RadialBar
                    background={{ fill: 'rgba(255,255,255,0.1)' }}
                    dataKey="value"
                    cornerRadius={10}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Pillar Scores */}
        {dashboardData.pillars && dashboardData.pillars.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {dashboardData.pillars.map((pillar) => (
              <div 
                key={pillar.name}
                className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20"
                data-testid={`pillar-card-${pillar.name.toLowerCase()}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${pillar.color}30` }}
                  >
                    {pillar.name === 'Authenticity' && <Sparkles className="w-5 h-5" style={{ color: pillar.color }} />}
                    {pillar.name === 'Accountability' && <Target className="w-5 h-5" style={{ color: pillar.color }} />}
                    {pillar.name === 'Community' && <Users className="w-5 h-5" style={{ color: pillar.color }} />}
                  </div>
                  <span className="text-white font-medium">{pillar.name}</span>
                </div>
                
                <p className="text-4xl font-bold text-white mb-2">{pillar.score?.toFixed(1)}</p>
                
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${pillar.score}%`,
                      backgroundColor: pillar.color
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Trend Chart */}
        {dashboardData.trend_data && dashboardData.trend_data.length > 0 && (
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 mb-8" data-testid="trend-chart">
            <h2 className="text-xl font-bold text-white mb-6">Score Trend</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dashboardData.trend_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    stroke="rgba(255,255,255,0.5)"
                    tick={{ fill: 'rgba(255,255,255,0.7)' }}
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    stroke="rgba(255,255,255,0.5)"
                    tick={{ fill: 'rgba(255,255,255,0.7)' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0,0,0,0.8)', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: 'white'
                    }}
                    labelFormatter={(v) => new Date(v).toLocaleDateString()}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="trust_score" 
                    name="Trust Score"
                    stroke="#8b5cf6" 
                    strokeWidth={3}
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="authenticity" 
                    name="Authenticity"
                    stroke="#a855f7" 
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray="5 5"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="accountability" 
                    name="Accountability"
                    stroke="#10b981" 
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray="5 5"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="community" 
                    name="Community"
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={false}
                    strokeDasharray="5 5"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Data Completeness */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-300 text-sm">Data Completeness</p>
              <p className="text-2xl font-bold text-white">{dashboardData.data_completeness?.toFixed(1)}%</p>
            </div>
            <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"
                style={{ width: `${dashboardData.data_completeness}%` }}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black/30 border-t border-white/10 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <p className="text-gray-400 text-sm">
              Powered by Trust Index Platform
            </p>
            <a 
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm transition"
            >
              Learn More <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default SharedDashboardPage;
